import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, DownloadSimple, Lightning, Copy, Lock, VideoCamera, Spinner, XCircle, IdentificationCard, User, EnvelopeSimple, Phone, Eye, EyeSlash, Key, ArrowSquareOut, FloppyDisk } from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";
import ProtectedVideo from "@/components/ProtectedVideo";
import { DirectBuyFunnel } from "@/components/videobuy/DirectBuyFunnel";

// ==== Validação CPF ====
function validateCPF(cpf) {
  const d = (cpf || "").replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const size of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < size; i++) sum += parseInt(d[i], 10) * (size + 1 - i);
    let rem = (sum * 10) % 11;
    if (rem === 10) rem = 0;
    if (rem !== parseInt(d[size], 10)) return false;
  }
  return true;
}

function formatCPF(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
}

export default function PublicFunnel() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const refCode = sp.get("ref") || null;
  const [funnel, setFunnel] = useState(null);
  // Novo fluxo: video → plans → quickform (nome+email) → checkout → signup (senha+cpf+tel) → reveal
  const [step, setStep] = useState("video");
  const [form, setForm] = useState({ name: "", email: "", phone: "", cpf: "", password: "", confirmPassword: "" });
  const [showPw, setShowPw] = useState(false);
  const [cpfTouched, setCpfTouched] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [watchPct, setWatchPct] = useState(0);
  const pollRef = useRef(null);

  const cpfDigits = form.cpf.replace(/\D/g, "");
  const cpfValid = useMemo(() => cpfDigits.length === 11 && validateCPF(cpfDigits), [cpfDigits]);
  const pwValid = form.password.length >= 6;
  const pwMatch = pwValid && form.password === form.confirmPassword;
  const cpfHasError = cpfTouched && cpfDigits.length > 0 && !cpfValid;

  const formatPhone = (v) => {
    let d = v.replace(/\D/g, "");
    if (d.startsWith("55")) d = d.slice(2);
    d = d.slice(0, 11);
    if (d.length === 0) return "";
    if (d.length <= 2) return `+55 ${d}`;
    if (d.length <= 7) return `+55 ${d.slice(0, 2)} ${d.slice(2)}`;
    return `+55 ${d.slice(0, 2)} ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  useEffect(() => {
    if (step !== "checkout" || !checkout) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/public/funnels/${slug}/status/${checkout.tx_id}`);
        if (data.status === "approved") {
          clearInterval(pollRef.current);
          // Se plano é grátis (sem cobrança real), pula direto pro reveal
          if (checkout.amount === 0) {
            setReveal(data.reveal);
            setStep("reveal");
          } else {
            setStep("signup");  // Novo passo: completar cadastro após pagar
          }
          toast.success("Pagamento confirmado! Complete seu cadastro para acessar.");
        }
      } catch (e) { console.warn("funnel poll failed:", e?.message || e); }
    }, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [step, checkout, slug]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/funnels/${slug}`);
        setFunnel(data);
        setPlans(data.plans || []);
      } catch { setFunnel({ notfound: true }); }
    })();
  }, [slug]);

  const submitQuickLead = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe seu nome");
    if (!form.email.trim()) return toast.error("Informe seu email");
    setLoading(true);
    try {
      const { data } = await api.post(`/public/funnels/${slug}/lead`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        ref: refCode,
      });
      setLeadId(data.lead_id);
      // Se ainda não escolheu plano, mostra opções
      if (selectedPlan === null) {
        setStep("plans");
        toast.success("Escolha um plano para pagar");
      } else {
        // Já escolheu plano antes (fluxo alternativo)
        await proceedToPayment(data.lead_id, selectedPlan);
      }
    } catch (err) {
      toast.error(errMsg(err, "Erro ao cadastrar"));
    } finally { setLoading(false); }
  };

  const proceedToPayment = async (lid, idx) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/funnels/${slug}/pay`, { lead_id: lid, plan_index: idx });
      if (data.free && data.reveal) { setReveal(data.reveal); setStep("reveal"); return; }
      setCheckout(data);
      setStep("checkout");
    } catch (err) {
      toast.error(errMsg(err, "Erro ao gerar pagamento"));
    } finally { setLoading(false); }
  };

  const choosePlan = async (idx) => {
    setSelectedPlan(idx);
    // Se ainda não temos lead, pede cadastro rápido (nome+email); senão vai direto pro pagamento
    if (!leadId) {
      setStep("quickform");
    } else {
      await proceedToPayment(leadId, idx);
    }
  };

  const confirmPayment = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/funnels/${slug}/confirm/${checkout.tx_id}`);
      if (checkout.amount === 0) {
        setReveal(data.reveal);
        setStep("reveal");
        toast.success("Acesso liberado!");
      } else {
        setStep("signup");
        toast.success("Pagamento confirmado! Complete seu cadastro.");
      }
    } catch (err) {
      toast.error(errMsg(err, "Erro ao confirmar pagamento"));
    } finally { setLoading(false); }
  };

  const submitFullSignup = async (e) => {
    e.preventDefault();
    setCpfTouched(true);
    if (form.cpf && !cpfValid) return toast.error("CPF inválido. Deixe em branco ou corrija.");
    if (form.password.length < 6) return toast.error("Senha precisa ter pelo menos 6 caracteres");
    if (form.password !== form.confirmPassword) return toast.error("As senhas não conferem");
    setLoading(true);
    try {
      const { data } = await api.post(`/public/funnels/${slug}/complete-signup/${checkout.tx_id}`, {
        name: form.name,
        phone: form.phone,
        cpf: cpfDigits || null,
        password: form.password,
        confirm_password: form.confirmPassword,
      });
      // 🚀 AUTO-LOGIN: backend retorna auto_login_token — salva já no localStorage
      // para que o botão "Acessar meu escritório" leve o usuário DIRETO pra área do membro
      // sem precisar digitar email+senha de novo (elimina latência percebida).
      if (data.auto_login_token && data.member) {
        try {
          localStorage.setItem("vb_member_token", data.auto_login_token);
          localStorage.setItem("vb_member", JSON.stringify(data.member));
        } catch (_e) { /* ignora se privacy mode */ }
      }
      setReveal({
        ...(data.reveal || {}),
        member_email: data.member_email,
        member_password: form.password,
        member_login_url: data.member_login_url || publicUrl("/member/login"),
        auto_login_token: data.auto_login_token,  // usado pelo botão "Acessar agora"
      });
      setStep("reveal");
      toast.success("Cadastro concluído! Sua área está liberada.");
    } catch (err) {
      toast.error(errMsg(err, "Erro ao concluir cadastro"));
    } finally { setLoading(false); }
  };

  const copyPix = () => { navigator.clipboard.writeText(checkout.qr_code); toast.success("Código Pix copiado"); };

  if (!funnel) return <div className="min-h-screen flex items-center justify-center text-white/60">Carregando...</div>;
  if (funnel.notfound) return <div className="min-h-screen flex items-center justify-center text-white/60">Página não encontrada.</div>;
  if (funnel.direct_buy_enabled) return <DirectBuyFunnel funnel={funnel} slug={slug} refCode={refCode} />;

  const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none";
  const currentPlan = selectedPlan !== null ? plans[selectedPlan] : null;
  const requiredPct = funnel.require_watch_percent ?? 95;
  const canProceed = watchPct >= requiredPct;

  return (
    <div className="min-h-screen hero-grid-bg px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Lightning size={20} color="var(--accent)" weight="fill" />
          <span className="font-display font-bold text-sm text-white/60">Powered by MERCADOPAGU</span>
        </div>

        <div className="flex items-center justify-center gap-2 mb-8 text-xs flex-wrap">
          {["Vídeo", "Plano", "Pagamento", "Cadastro", "Acesso"].map((label, i) => {
            const stepMap = { video: 0, plans: 1, quickform: 1, checkout: 2, signup: 3, reveal: 4 };
            const active = stepMap[step] >= i;
            return (
              <React.Fragment key={i}>
                <div className={`px-2 py-1 rounded-sm ${active ? "bg-[var(--accent)] text-black font-bold" : "border border-[var(--border)] text-white/40"}`}>{label}</div>
                {i < 4 && <div className="w-4 h-px bg-[var(--border)]" />}
              </React.Fragment>
            );
          })}
        </div>

        {step === "video" && (
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-center mb-8" data-testid="funnel-public-title">{funnel.title}</h1>
        )}

        {step === "video" && (
          <div className="fadeUp">
            <ProtectedVideo url={funnel.video_url} onProgress={setWatchPct} />
            <div className="mt-4">
              <div className="flex justify-between text-xs text-white/60 mb-1">
                <span>Progresso do vídeo</span>
                <span data-testid="watch-progress">{watchPct}% / {requiredPct}%</span>
              </div>
              <div className="w-full h-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm overflow-hidden">
                <div className="h-full bg-[var(--accent)] transition-all" style={{ width: `${Math.min(100, (watchPct / requiredPct) * 100)}%` }} />
              </div>
            </div>
            <button
              onClick={() => setStep("plans")}
              disabled={!canProceed}
              className="btn-primary w-full mt-6 text-lg disabled:opacity-40"
              data-testid="funnel-next-btn"
            >
              {canProceed ? (funnel.cta_text || "Continuar") : `Assista ${requiredPct}% para desbloquear`}
            </button>
          </div>
        )}

        {step === "quickform" && (
          <div className="card-flat p-6 md:p-8 fadeUp" data-testid="quickform-card">
            <div className="label-caps mb-2 flex items-center gap-1"><User size={12} /> Passo rápido</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl mb-2">Só precisamos de 2 dados</h2>
            <p className="text-white/60 text-sm mb-6">
              Preenchimento completo (CPF, telefone, senha) fica <b>para depois do pagamento</b> — assim você garante seu acesso primeiro.
            </p>
            {selectedPlan !== null && plans[selectedPlan] && (
              <div className="border border-[var(--border)] rounded-sm p-3 mb-4 flex items-center justify-between bg-[var(--bg-elev)]">
                <div>
                  <div className="text-xs opacity-60">Plano selecionado</div>
                  <div className="font-semibold">{plans[selectedPlan].name}</div>
                </div>
                <div className="font-black text-[var(--accent)]">{brl(plans[selectedPlan].price)}</div>
              </div>
            )}
            <form onSubmit={submitQuickLead} className="space-y-4">
              <div>
                <label className="label-caps block mb-2 flex items-center gap-1"><User size={11} /> Seu nome</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-name"
                  className={inp}
                  placeholder="Como quer ser chamado"
                />
              </div>
              <div>
                <label className="label-caps block mb-2 flex items-center gap-1"><EnvelopeSimple size={11} /> E-mail</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="lead-email"
                  className={inp}
                  placeholder="Enviamos o recibo para este e-mail"
                />
              </div>
              <div className="flex items-start gap-2 text-[11px] text-white/50 pt-2">
                <Lock size={13} className="mt-0.5 shrink-0" color="var(--accent)" />
                <span>Dados criptografados. Nunca compartilhamos com terceiros.</span>
              </div>
              <button
                disabled={loading || !form.name.trim() || !form.email.trim()}
                data-testid="lead-submit"
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Aguarde..." : "Ir para pagamento"}
              </button>
            </form>
          </div>
        )}

        {step === "signup" && (
          <div className="card-flat p-6 md:p-8 fadeUp" data-testid="signup-card">
            <div className="label-caps mb-2 flex items-center gap-1">
              <CheckCircle size={12} weight="fill" color="var(--success)" /> Pagamento aprovado
            </div>
            <h2 className="font-display font-bold text-2xl md:text-3xl mb-2">Complete seu cadastro</h2>
            <p className="text-white/60 text-sm mb-6">
              Falta pouco! Escolha sua senha e complete seus dados para acessar sua área permanente.
            </p>
            <form onSubmit={submitFullSignup} className="space-y-4">
              <div>
                <label className="label-caps block mb-2 flex items-center gap-1"><User size={11} /> Nome completo</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="signup-name"
                  className={inp}
                  placeholder="Como aparece no seu documento"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2 flex items-center gap-1"><Phone size={11} /> WhatsApp</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                    data-testid="signup-phone"
                    className={inp}
                    placeholder="+55 11 99999-9999"
                    inputMode="tel"
                    maxLength={17}
                  />
                </div>
                <div>
                  <label className="label-caps block mb-2 flex items-center gap-1"><IdentificationCard size={11} /> CPF (opcional)</label>
                  <input
                    value={form.cpf}
                    onChange={(e) => {
                      setForm({ ...form, cpf: formatCPF(e.target.value) });
                      if (cpfTouched) setCpfTouched(false);
                    }}
                    onBlur={() => setCpfTouched(true)}
                    data-testid="signup-cpf"
                    className={`${inp} ${cpfHasError ? "!border-[var(--danger)]" : ""}`}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    maxLength={14}
                  />
                  {cpfHasError && (
                    <div className="text-xs text-[var(--danger)] mt-1.5 flex items-center gap-1">
                      <XCircle size={12} weight="fill" /> CPF inválido
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <div className="label-caps mb-3 flex items-center gap-1"><Key size={11} /> Crie sua senha de acesso</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="label-caps block mb-2">Senha (mín 6)</label>
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        required
                        minLength={6}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="signup-password"
                        className={`${inp} pr-10 ${form.password && !pwValid ? "!border-[var(--danger)]" : form.password && pwValid ? "!border-[var(--success)]" : ""}`}
                        placeholder="Escolha uma senha"
                      />
                      <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                        {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label-caps block mb-2">Confirmar senha</label>
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        required
                        minLength={6}
                        value={form.confirmPassword}
                        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                        data-testid="signup-password-confirm"
                        className={`${inp} pr-10 ${form.confirmPassword && !pwMatch ? "!border-[var(--danger)]" : pwMatch && form.confirmPassword ? "!border-[var(--success)]" : ""}`}
                        placeholder="Digite de novo"
                      />
                      {form.confirmPassword && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          {pwMatch ? <CheckCircle size={16} weight="fill" color="var(--success)" /> : <XCircle size={16} weight="fill" color="var(--danger)" />}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-white/40 mt-2">
                  Esta senha será usada para entrar na sua área de conteúdo. <b className="text-white/70">Anote em local seguro.</b>
                </div>
              </div>
              <button
                disabled={loading || !pwValid || !pwMatch}
                data-testid="signup-submit"
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Concluindo..." : "Concluir cadastro e liberar acesso"}
              </button>
            </form>
          </div>
        )}

        {step === "plans" && (
          <div className="fadeUp">
            <div className="text-center mb-6">
              <div className="label-caps">Passo 3</div>
              <h2 className="font-display font-black text-3xl mb-2">Escolha seu plano</h2>
              <p className="text-white/60 text-sm">Pague com Pix para acessar o conteúdo.</p>
            </div>
            <div className={`grid gap-4 ${plans.length >= 3 ? "md:grid-cols-3" : plans.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
              {plans.map((p, i) => (
                <div key={`${p.name}-${p.price}-${i}`} className="card-flat p-6 text-center flex flex-col" data-testid={`public-plan-${i}`}>
                  <div className="font-display font-bold text-lg">{p.name}</div>
                  <div className="font-display font-black text-4xl text-[var(--accent)] my-4">{brl(p.price)}</div>
                  <div className="text-sm text-white/60 flex-1">{p.description}</div>
                  <button onClick={() => choosePlan(i)} disabled={loading} className="btn-primary w-full mt-6" data-testid={`buy-plan-${i}`}>
                    {loading && selectedPlan === i ? "Gerando..." : "Comprar com Pix"}
                  </button>
                </div>
              ))}
            </div>
            {plans.length === 0 && <div className="card-flat p-8 text-center text-white/60">Este funil ainda não tem planos configurados.</div>}
          </div>
        )}

        {step === "checkout" && checkout && (
          <div className="card-flat p-8 fadeUp text-center">
            <Lock size={40} color="var(--accent)" weight="duotone" className="mx-auto" />
            <div className="label-caps mt-4 mb-2">Pagamento seguro · Mercado Pago</div>
            <h2 className="font-display font-bold text-2xl mb-1">{currentPlan?.name}</h2>
            <div className="font-display font-black text-5xl text-[var(--accent)] my-4">{brl(checkout.amount)}</div>
            {checkout.mock && (
              <div className="label-caps mb-4 inline-flex px-3 py-1 border border-[var(--accent)] text-[var(--accent)] rounded-sm">
                Sandbox · MP não configurado
              </div>
            )}
            <div className="inline-block bg-white p-4 rounded-sm">
              {checkout.qr_code_base64 ? (
                <img src={`data:image/png;base64,${checkout.qr_code_base64}`} alt="Pix" className="w-56 h-56" />
              ) : (
                <QRCodeSVG value={checkout.qr_code || "MOCK"} size={224} bgColor="#fff" fgColor="#050709" />
              )}
            </div>
            <div className="mt-4 flex items-center gap-2 max-w-md mx-auto">
              <div className="flex-1 text-xs font-mono bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-3 py-2 text-white/70 truncate">{checkout.qr_code}</div>
              <button onClick={copyPix} className="btn-ghost text-xs px-3 py-2 flex items-center gap-1" data-testid="funnel-copy-pix"><Copy size={14} /> Copiar</button>
            </div>
            <div className="mt-6 border border-[var(--accent)] bg-[var(--accent)]/5 rounded-sm p-4 flex items-center gap-3 max-w-md mx-auto" data-testid="funnel-waiting">
              <Spinner size={22} color="var(--accent)" className="animate-spin" />
              <div className="text-sm text-white/80 text-left">
                <b>Aguardando pagamento…</b>
                <div className="text-xs text-white/60 mt-1">Assim que o Pix for pago, o conteúdo será liberado automaticamente.</div>
              </div>
            </div>
            <div className="mt-4 flex flex-col md:flex-row gap-2 max-w-md mx-auto">
              <button
                onClick={confirmPayment}
                disabled={loading}
                className="btn-primary flex-1 text-sm disabled:opacity-50"
                data-testid="funnel-already-paid-btn"
                title="Se você já pagou e o sistema não liberou automaticamente, clique aqui"
              >
                {loading ? "Verificando..." : "✅ Já paguei · verificar agora"}
              </button>
              <button onClick={() => setStep("plans")} className="btn-ghost text-sm">
                ← Voltar aos planos
              </button>
            </div>
            <div className="text-[11px] text-white/40 mt-3 max-w-md mx-auto">
              Em alguns casos o Pix demora 30-60s pra ser confirmado. Se pagou e a tela não mudou, use o botão acima.
            </div>
          </div>
        )}

        {step === "reveal" && reveal && (
          <div className="fadeUp space-y-6">
            <div className="card-flat p-8 text-center border-l-2 border-l-[var(--success)]">
              <CheckCircle size={64} color="var(--success)" weight="fill" className="mx-auto" />
              <div className="font-display font-black text-3xl mt-4">Pagamento aprovado!</div>
              <p className="text-white/60 mt-2">Anote seus dados de acesso abaixo. <b className="text-white">Salve em local seguro</b> — você usará para entrar no seu escritório sempre que quiser.</p>
            </div>

            {/* CARD DE CREDENCIAIS */}
            {(reveal.member_email || reveal.member_password || reveal.member_login_url) && (
              <div className="card-flat p-6 border-l-2 border-l-[var(--accent)]" data-testid="reveal-credentials">
                <div className="mb-4 border border-[var(--danger)]/40 bg-[var(--danger)]/5 rounded-sm p-3 flex items-start gap-2 text-sm">
                  <XCircle size={20} weight="fill" color="var(--danger)" className="mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-[var(--danger)]">⚠️ Não perca essas informações!</div>
                    <div className="text-white/70 text-xs mt-1">
                      <b className="text-white">Nós não enviamos por e-mail.</b> Seu acesso é <b>aqui</b>. Copie e guarde em local seguro (bloco de notas, gerenciador de senhas, foto…).
                    </div>
                  </div>
                </div>

                <div className="label-caps mb-2 flex items-center gap-1">
                  <Key size={12} /> Seus dados de acesso ao escritório
                </div>
                <div className="font-display font-black text-2xl mb-5">Salve este acesso</div>

                {/* Link */}
                <div className="mb-3">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1.5">Link do escritório</div>
                  <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-sm px-3 py-2.5">
                    <ArrowSquareOut size={14} className="text-[var(--accent)] shrink-0" />
                    <a
                      href={reveal.member_login_url || "/member/login"}
                      target="_blank" rel="noreferrer"
                      className="text-sm text-[var(--accent)] hover:underline truncate flex-1"
                      data-testid="reveal-link"
                    >
                      {reveal.member_login_url || publicUrl("/member/login")}
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(reveal.member_login_url || publicUrl("/member/login"));
                        toast.success("Link copiado!");
                      }}
                      data-testid="copy-link"
                      className="p-1.5 rounded-sm hover:bg-white/5 text-white/60 hover:text-white"
                      title="Copiar"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>

                {/* E-mail */}
                {reveal.member_email && (
                  <div className="mb-3">
                    <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1.5">Seu login (e-mail)</div>
                    <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-sm px-3 py-2.5">
                      <EnvelopeSimple size={14} className="text-white/50 shrink-0" />
                      <span className="text-sm font-mono flex-1 truncate" data-testid="reveal-email">{reveal.member_email}</span>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(reveal.member_email); toast.success("E-mail copiado!"); }}
                        data-testid="copy-email"
                        className="p-1.5 rounded-sm hover:bg-white/5 text-white/60 hover:text-white"
                        title="Copiar"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Senha */}
                {reveal.member_password && (
                  <div className="mb-4">
                    <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1.5">Sua senha</div>
                    <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-sm px-3 py-2.5">
                      <Key size={14} className="text-white/50 shrink-0" />
                      <span className="text-sm font-mono flex-1 truncate" data-testid="reveal-password">{reveal.member_password}</span>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(reveal.member_password); toast.success("Senha copiada!"); }}
                        data-testid="copy-password"
                        className="p-1.5 rounded-sm hover:bg-white/5 text-white/60 hover:text-white"
                        title="Copiar"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Copy tudo + acessar */}
                <div className="flex flex-col md:flex-row gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const txt = `MercadoPagu — Acesso ao escritório\n\nLink: ${reveal.member_login_url || publicUrl("/member/login")}\nE-mail: ${reveal.member_email || ""}\nSenha: ${reveal.member_password || form.password}`;
                      navigator.clipboard.writeText(txt);
                      toast.success("Todos os dados copiados!");
                    }}
                    data-testid="copy-all"
                    className="btn-ghost text-sm flex items-center justify-center gap-2"
                  >
                    <FloppyDisk size={14} /> Copiar tudo
                  </button>
                  <button
                    onClick={() => {
                      // Se temos auto_login_token, vai DIRETO pra área do membro (sem re-login)
                      if (reveal.auto_login_token) {
                        window.open("/member", "_blank", "noopener,noreferrer");
                      } else {
                        window.open(reveal.member_login_url || "/member/login", "_blank", "noopener,noreferrer");
                      }
                    }}
                    data-testid="reveal-member-login"
                    className="btn-primary flex items-center justify-center gap-2 flex-1"
                  >
                    <ArrowSquareOut size={14} weight="bold" />
                    {reveal.auto_login_token ? "Acessar meu escritório agora (auto-login)" : "Acessar meu escritório agora"}
                  </button>
                </div>
                <div className="text-[11px] text-white/50 mt-3 flex items-start gap-1.5">
                  <Lock size={11} className="mt-0.5 shrink-0" />
                  Esta é a única vez que essa senha aparece na tela. Guarde antes de sair.
                </div>
              </div>
            )}

            {reveal.files?.length > 0 && (
              <div>
                <div className="label-caps flex items-center gap-2 mb-4"><DownloadSimple size={14} /> Materiais para download</div>
                <div className="grid md:grid-cols-2 gap-4">
                  {reveal.files.map((f, i) => (
                    <div key={`rf-${i}`} className="card-flat p-4 flex items-center justify-between" data-testid={`reveal-file-${i}`}>
                      <div className="min-w-0 flex-1 mr-3">
                        <div className="font-display font-bold text-sm truncate">{f.name || `Arquivo ${i + 1}`}</div>
                        {f.description && <div className="text-xs text-white/60 mt-1 line-clamp-2">{f.description}</div>}
                      </div>
                      <a href={f.url} target="_blank" rel="noreferrer" className="btn-primary flex items-center gap-2 shrink-0" data-testid={`reveal-file-btn-${i}`}>
                        <DownloadSimple size={14} weight="bold" /> Baixar
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reveal.delivery_videos?.length > 0 && (
              <div>
                <div className="label-caps flex items-center gap-2 mb-4"><VideoCamera size={14} /> Área de vídeos exclusivos</div>
                <div className="grid md:grid-cols-2 gap-6">
                  {reveal.delivery_videos.map((v, i) => (
                    <div key={v.url || `rv-${i}`} className="card-flat p-4" data-testid={`delivery-video-${i}`}>
                      <div className="font-display font-bold text-lg mb-3">{v.name || `Vídeo ${i + 1}`}</div>
                      <ProtectedVideo url={v.url} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
